import { PrivateAgentCanonicalConversationResponseSchema, PrivateAgentInputSubmitRequestSchema, PrivateAgentInputAdmissionReceiptSchema, PrivateAgentClearChatRequestSchema, PrivateAgentClearChatReceiptSchema } from "@salesko/contracts";
import { PrivateAgentChatClientIntentSchema } from "@salesko/contracts";
import { PrivateAgentTurnCancelRequestSchema, PrivateAgentTurnEndRequestSchema, PrivateAgentTurnEndResponseSchema,
  PrivateAgentTurnRetryRequestSchema, PrivateAgentTurnRetryResponseSchema, PrivateAgentStopAndSendRequestSchema,
  PrivateAgentStopAndSendReceiptSchema, type PrivateAgentChatClientIntent, type PrivateAgentTurnCancelRequest } from "@salesko/contracts";
import type {
  ActivityBackfillCommand,
  ActivityBackfillResponse,
  ActivityFeedQuery,
  ActivityFeedResponse,
  ActivitySourceDetailResponse,
  AgentChatThreadListResponse,
  AgentChatThreadResponse,
  AgentChatThreadUpsertRequest,
  AgentGraphInput,
  AgentQuotaStatusResponse,
  AgentResearchEntitlementResponse,
  AgentResearchJobCancelResponse,
  AgentResearchJobCreateRequest,
  AgentResearchJobCreateResponse,
  AgentResearchJobListResponse,
  AgentResearchJobReadResponse,
  AgentResearchMaterializeChangeSetResponse,
  CalendarConnectionStatusResponse,
  CalendarConnectorAuthorizeUrlResponse,
  CalendarEventDismissResponse,
  CalendarProvider,
  EmailConnectionStatusResponse,
  EmailConnectorAuthorizeUrlResponse,
  EmailLeadCandidateConfirmRequest,
  EmailLeadCandidateConfirmResponse,
  EmailLeadCandidateDismissResponse,
  EmailLeadCandidateListResponse,
  EmailProvider,
  EmailReplyDraftCreateRequest,
  EmailReplyDraftCreateResponse,
  EmailReplyTemplateCreateRequest,
  EmailReplyTemplateListResponse,
  EmailReplyTemplateResponse,
  EmailReplyTemplateUpdateRequest,
  EmailSyncTriggerResponse,
  SaleskoBillingCatalogResponse,
  SaleskoBillingCheckoutResponse,
  SaleskoBillingCreditBalanceResponse,
  SaleskoBillingCreditHistoryResponse,
  SaleskoBillingEntitlementResponse,
  SaleskoScheduleResponse,
  GraphCanonicalEntityDetailResponse,
  GraphCanonicalEntityListResponse,
  GraphCanonicalEntityMergeRequest,
  GraphCanonicalEntityMergeResponse,
  GraphCanonicalEntityRekeyRequest,
  GraphCanonicalEntityRekeyResponse,
  GraphCanonicalEntitySplitRequest,
  GraphCanonicalEntitySplitResponse,
  GraphChangeSetAuditExportResponse,
  GraphChangeSetApplyResponse,
  GraphChangeSetAuditLogListResponse,
  GraphChangeSetCreateResponse,
  GraphChangeSetListResponse,
  GraphChangeSetRevertResponse,
  GraphChangeSetWrite,
  GraphCsvImportRequest,
  GraphCsvImportResponse,
  GraphEntityType,
  GraphFrameCommitResponse,
  GraphFrameExportFormat,
  GraphFrameExportResponse,
  GraphFrameImportResponse,
  GraphFrameJsonImportRequest,
  GraphFrameListResponse,
  GraphFrameObsidianImportRequest,
  GraphFrameRecord,
  GraphFrameShareLinkIssueRequest,
  GraphFrameShareLinkIssueResponse,
  GraphFrameShareReadResponse,
  GraphRelationshipPathsResponse,
  GraphRelationshipRemindersResponse,
  GraphFrameWrite,
  GraphNoteIntakeInput,
  GraphNoteIntakeJobRecord,
  GraphNoteIntakeResponse,
  GraphOpportunityActionRequest,
  GraphOpportunityActionResponse,
  GraphOpportunityFeedResponse,
  GraphOpportunityStatus,
  GraphProjectionResponse,
  GraphSearchResponse,
  GraphVisualizationFrame,
  McpConsentGrantIssueRequest,
  McpConsentGrantIssueResponse,
  MeetingPreparationErrorCode,
  MeetingPreparationRequest,
  MeetingPreparationResponse,
  PersonalAgentDeviceListResponse,
  PersonalAgentDeviceRevokeResponse,
  PersonalAgentPairingCodeResponse,
  PersonalAgentPresenceHintListResponse,
  PrivateAgentConversationResponse,
  PrivateAgentEventReplayResponse,
  PrivateAgentProfileResponse,
  PrivateAgentProfileWrite,
  PrivateAgentReadiness,
  PrivateAgentTurnResponse,
  ProviderAuthGrantIssueRequest,
  ProviderAuthGrantIssueResponse,
  SaleskoOAuthProvider,
  SaleskoSessionGrantIssueResponse,
  SaleskoAgentSettings,
  SaleskoAgentSettingsResponse,
  SaleskoTenantPolicyResponse,
  SaleskoTenantPolicyUpdateRequest,
  TransactionCommandResponse,
  TransactionCorrectionCommand,
  TransactionDetailResponse,
  TransactionGraphResponse,
  TransactionActivityCapabilityResponse,
  TransactionProposalExtractionRequest,
  TransactionProposalExtractionResponse,
  TransactionRecordCommand,
  TransactionStatusChangeCommand
} from "@salesko/contracts";
import { PrivateAgentListResponseSchema, PrivateAgentProfileResponsePhase3Schema, type PrivateAgentListResponse, type PrivateAgentProfileResponsePhase3 } from "@salesko/contracts/private-agent-profile-projection";
import { PrivateAgentAgentIdSchema, PrivateAgentCreateRequestSchema, PrivateAgentProfileWriteSchema, PrivateAgentPlacementUpdateRequestSchema, type PrivateAgentCreateRequest, type PrivateAgentPlacementUpdateRequest } from "@salesko/contracts";
import {
  ActivityBackfillCommandSchema,
  ActivityBackfillResponseSchema,
  ActivityFeedQuerySchema,
  ActivityFeedResponseSchema,
  ActivitySourceDetailResponseSchema,
  AgentResearchJobCancelResponseSchema,
  AgentResearchJobCreateRequestSchema,
  AgentResearchJobCreateResponseSchema,
  AgentResearchJobListResponseSchema,
  AgentResearchJobReadResponseSchema,
  AgentResearchMaterializeChangeSetResponseSchema,
  PrivateAgentConversationResponseSchema,
  PrivateAgentEventReplayResponseSchema,
  PrivateAgentChatNotReadyErrorCode,
  PrivateAgentReadinessResponseSchema,
  PrivateAgentReadinessSchema,
  PrivateAgentTurnResponseSchema,
  McpConsentGrantIssueResponseSchema,
  MeetingPreparationErrorResponseSchema,
  MeetingPreparationResponseSchema,
  SaleskoOAuthProviders,
  SaleskoSupabaseOAuthProviders,
  TransactionCommandResponseSchema,
  TransactionCorrectionCommandSchema,
  TransactionDetailResponseSchema,
  TransactionGraphResponseSchema,
  TransactionActivityCapabilityResponseSchema,
  TransactionProposalExtractionRequestSchema,
  TransactionProposalExtractionResponseSchema,
  TransactionRecordCommandSchema,
  TransactionStatusChangeCommandSchema
} from "@salesko/contracts";

type RuntimeImportMetaEnv = {
  VITE_AGENT_API_URL?: string;
  VITE_API_BASE_URL?: string;
  VITE_AUTH_PROVIDER?: string;
  VITE_AUTH_API_BASE_URL?: string;
  VITE_MCP_BASE_URL?: string;
  VITE_SALESKO_AUTH_TENANT_ID?: string;
};

type SaleskoSessionGrantStorage = Pick<Storage, "getItem" | "removeItem" | "setItem">;

export type StoredSaleskoSession = {
  email?: string;
  expiresAt: string;
  name?: string;
  tenantId: string;
  token: string;
  tokenType: "Bearer";
  userId: string;
};

export type PendingBillingCheckoutIntent = {
  createdAt: string;
  productId: string;
  returnPath: string;
};

export type BinaryArtifactDownload = {
  blob: Blob;
  contentType: string;
  filename: string;
};

export type GoogleAuthCallbackResult =
  | {
      status: "success";
      session: StoredSaleskoSession;
    }
  | {
      error: string;
      status: "error";
    };

type BetterAuthSignInSocialResponse = {
  redirect?: boolean;
  url?: unknown;
};

const runtimeEnv = (import.meta as ImportMeta & { env?: RuntimeImportMetaEnv }).env ?? {};
const sessionStorageKey = "salesko.sessionGrant";
const pendingBillingCheckoutStorageKey = "salesko.pendingBillingCheckout";
const pendingBillingCheckoutMaxAgeMs = 24 * 60 * 60 * 1000;
const stableBetterAuthUserIdPrefix = "better-auth-email:";
export const SaleskoSessionChangeEvent = "salesko-session-change" as const;
const sessionGrantStorageKeys = [
  sessionStorageKey,
  "salesko.session_grant",
  "salesko.sessionGrant.token",
  "salesko.session.token"
] as const;
const legacyApiBaseUrl = "https://salesko-api.metalabs.workers.dev";
const agentBaseUrl = runtimeEnv.VITE_AGENT_API_URL ?? localWorkerUrl(8788) ?? "https://agent.salesko.ai";
const apiBaseUrl = runtimeEnv.VITE_API_BASE_URL ?? localWorkerUrl(8787) ?? "https://api.salesko.ai";
const mcpBaseUrl = runtimeEnv.VITE_MCP_BASE_URL ?? localWorkerUrl(8789) ?? "https://mcp.salesko.ai";
const authApiBaseUrl = runtimeEnv.VITE_AUTH_API_BASE_URL ?? localWorkerUrl(8787) ?? legacyApiBaseUrl;
export const authProviderMode = runtimeEnv.VITE_AUTH_PROVIDER ?? "none";
const saleskoTenantId = runtimeEnv.VITE_SALESKO_AUTH_TENANT_ID ?? "salesko";
const authCallbackPath = "/auth/google/callback";
const allowedSupabaseOAuthProviders = new Set<string>(SaleskoSupabaseOAuthProviders);

export const saleskoAuthProviders = SaleskoOAuthProviders;

function localWorkerUrl(port: number): string | null {
  if (typeof window === "undefined") {
    return null;
  }

  if (window.location.hostname === "127.0.0.1" || window.location.hostname === "localhost") {
    return `http://${window.location.hostname}:${port}`;
  }

  return null;
}

export async function createGraphFrame(input: AgentGraphInput): Promise<GraphVisualizationFrame> {
  return postJson<GraphVisualizationFrame>(`${agentBaseUrl}/api/agent/graph-frame`, input);
}

export function agentChatStreamEndpoint(): string {
  return `${agentBaseUrl}/api/agent/chat/stream`;
}

export function authProviderAvailable(provider: SaleskoOAuthProvider): boolean {
  if (authProviderMode === "better-auth") {
    return provider === "google" || provider === "linkedin";
  }

  return false;
}

export function authStartUrl(provider: SaleskoOAuthProvider): string | null {
  void provider;
  return null;
}

export function googleAuthStartUrl(): string {
  const startUrl = authStartUrl("google");
  if (!startUrl) {
    throw new Error("google_auth_unavailable");
  }

  return startUrl;
}

export async function startAuthProviderSignIn(provider: SaleskoOAuthProvider): Promise<boolean> {
  if (authProviderMode === "better-auth") {
    return startBetterAuthProviderSignIn(provider);
  }

  const startUrl = authStartUrl(provider);
  if (!startUrl) {
    return false;
  }

  window.location.assign(startUrl);
  return true;
}

export async function exchangeBetterAuthSessionGrant(): Promise<StoredSaleskoSession> {
  const response = await fetch(`${authApiBaseUrl}/api/auth/salesko-session`, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      "X-Salesko-Session-Tenant-Id": saleskoTenantId
    },
    body: "{}"
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Better Auth session exchange failed with ${response.status}`);
  }

  const body = (await response.json()) as SaleskoSessionGrantIssueResponse;
  return {
    expiresAt: body.expiresAt,
    ...(body.grant.name ? { name: body.grant.name } : {}),
    tenantId: body.grant.tenantId,
    token: body.token,
    tokenType: body.tokenType,
    userId: body.grant.userId
  };
}

/**
 * Mints provider-auth headers for the agent chat stream from the stored session
 * grant. An unauthenticated visitor (no session token) legitimately has no
 * grant to mint and gets `undefined`. A visitor who *is* signed in but whose
 * grant issuance fails (expired session, apps/api misconfiguration, network
 * error) is a real error, not an anonymous request: it throws so callers can
 * surface the reason instead of silently sending zero auth headers — which the
 * agent worker cannot distinguish from "never attempted to authenticate".
 */
export async function agentProviderAuthHeaders(
  sessionToken: string | null = readSaleskoSessionGrantToken()
): Promise<HeadersInit | undefined> {
  if (!sessionToken) {
    return undefined;
  }

  const grant = await issueProviderAuthGrant({ ttlSeconds: 300 }, sessionToken);
  if (!grant) {
    return undefined;
  }

  return {
    Authorization: `${grant.tokenType} ${grant.token}`
  };
}

export async function issueProviderAuthGrant(
  input: ProviderAuthGrantIssueRequest = {},
  sessionToken: string | null = readSaleskoSessionGrantToken()
): Promise<ProviderAuthGrantIssueResponse | null> {
  if (!sessionToken) {
    return null;
  }

  return postJson<ProviderAuthGrantIssueResponse>(`${apiBaseUrl}/api/provider-grants`, input, {
    Authorization: `Bearer ${sessionToken}`
  });
}

/**
 * Validates a `/mcp-consent?return_to=` value against the MCP worker's own
 * `/authorize` endpoint before it is ever redirected back to. `new URL(raw)`
 * is called without a base, so a relative path throws instead of silently
 * resolving against this page's origin, and the parsed result must match the
 * configured MCP worker's origin and exactly the `/authorize` path. Returns
 * null (never throws) for anything else — cross-origin targets, other paths,
 * and unparseable strings all fall into the same "invalid link" bucket the
 * consent route renders, rather than guessing a fallback destination.
 */
export function validateMcpAuthorizeReturnTo(raw: string | null): URL | null {
  if (!raw) {
    return null;
  }

  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }

  let mcpOrigin: string;
  try {
    mcpOrigin = new URL(mcpBaseUrl).origin;
  } catch {
    return null;
  }

  if (url.origin !== mcpOrigin || url.pathname !== "/authorize") {
    return null;
  }

  return url;
}

/**
 * Issues a signed MCP consent grant token for the `/mcp-consent` handoff
 * page. Mirrors `issueProviderAuthGrant`'s POST shape; the token is embedded
 * into the MCP worker's `/authorize?salesko_consent=<token>` redirect. The
 * response is parsed through the shared Zod schema (rather than a bare
 * generic cast like the other `postJson` callers use) because this token is
 * about to be handed back to a third-party MCP client's OAuth flow, so a
 * malformed API response should fail loudly here instead of surfacing as a
 * confusing failure downstream.
 */
export async function issueMcpConsentGrant(
  input: McpConsentGrantIssueRequest,
  sessionToken: string
): Promise<McpConsentGrantIssueResponse> {
  const body = await postJson<unknown>(`${apiBaseUrl}/api/mcp/consent-grants`, input, {
    Authorization: `Bearer ${sessionToken}`
  });
  return McpConsentGrantIssueResponseSchema.parse(body);
}

/** Reads the signed-in tenant's daily Generic Agent quota for display in the
 * account menu. Reuses the chat stream's provider-grant auth and the agent
 * worker's read-only GET /api/agent/quota (no counter increment). Returns null
 * when signed out — there is no grant to mint — so callers hide the indicator.
 * The quota indicator is a non-critical display, so a provider-auth failure is
 * tolerated here (hide the indicator) rather than propagated; the note-intake
 * path, by contrast, lets that same failure surface and fails closed. */
export async function getAgentQuota(): Promise<AgentQuotaStatusResponse | null> {
  let headers: HeadersInit | undefined;
  try {
    headers = await agentProviderAuthHeaders();
  } catch {
    return null;
  }
  if (!headers) {
    return null;
  }
  return getJson<AgentQuotaStatusResponse>(`${agentBaseUrl}/api/agent/quota`, headers);
}

// Chat thread history lives in apps/api (PlanetScale), read/written with the
// session grant — distinct from the agent worker's provider-grant quota path.
export async function listAgentChatThreads(limit = 30): Promise<AgentChatThreadListResponse> {
  const url = new URL(`${apiBaseUrl}/api/agent/threads`);
  url.searchParams.set("limit", String(limit));
  return getJson<AgentChatThreadListResponse>(url.toString(), saleskoSessionAuthHeaders());
}

export async function readAgentChatThread(threadId: string): Promise<AgentChatThreadResponse> {
  return getJson<AgentChatThreadResponse>(
    `${apiBaseUrl}/api/agent/threads/${encodeURIComponent(threadId)}`,
    saleskoSessionAuthHeaders()
  );
}

export async function saveAgentChatThread(
  threadId: string,
  input: AgentChatThreadUpsertRequest
): Promise<AgentChatThreadResponse> {
  return putJson<AgentChatThreadResponse>(
    `${apiBaseUrl}/api/agent/threads/${encodeURIComponent(threadId)}`,
    input,
    saleskoSessionAuthHeaders()
  );
}

export async function deleteAgentChatThread(threadId: string): Promise<void> {
  const response = await fetch(`${apiBaseUrl}/api/agent/threads/${encodeURIComponent(threadId)}`, {
    method: "DELETE",
    headers: new Headers(saleskoSessionAuthHeaders())
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Request failed with ${response.status}`);
  }
}

/**
 * Private Agent Chat is an App-owned, exact-runtime conversation surface. It
 * intentionally does not share Generic Agent threads, provider grants, or the
 * streaming endpoint: every request stays on the authenticated private-chat
 * API that owns the frozen Profile/device/runtime binding.
 */
export async function readPrivateAgentCanonicalConversation(agentId: string) {
  const response = PrivateAgentCanonicalConversationResponseSchema.parse(await agentResearchRequest<unknown>(
    `${apiBaseUrl}/api/agent/private-chat/agents/${PrivateAgentAgentIdSchema.parse(agentId)}/conversation`,
    { method: "GET", headers: saleskoSessionAuthHeaders() }));
  if (response.conversation && response.conversation.agentId !== agentId) throw new Error("Conversation belongs to a different Agent.");
  return response;
}

export async function readPrivateAgentChatConversation(conversationId: string, epoch: number, beforeSequence?: number): Promise<PrivateAgentConversationResponse> {
  const url = new URL(`${apiBaseUrl}/api/agent/private-chat/conversations/${encodeURIComponent(conversationId)}/messages`);
  url.searchParams.set("epoch", String(epoch));
  url.searchParams.set("limit", "100");
  if (beforeSequence !== undefined) url.searchParams.set("beforeSequence", String(beforeSequence));
  const response = PrivateAgentConversationResponseSchema.parse(await agentResearchRequest<unknown>(url.toString(), { method: "GET", headers: saleskoSessionAuthHeaders() }));
  if (response.conversation.conversationId !== conversationId || response.contextEpoch !== epoch) throw new Error("Conversation snapshot identity mismatch.");
  return response;
}

export async function submitPrivateAgentChatInput(agentId: string, input: import("@salesko/contracts").PrivateAgentChatClientIntent & {kind: "send"}) {
  return PrivateAgentInputAdmissionReceiptSchema.parse(await agentResearchRequest<unknown>(
    `${apiBaseUrl}/api/agent/private-chat/agents/${PrivateAgentAgentIdSchema.parse(agentId)}/inputs`,
    { method: "POST", body: PrivateAgentInputSubmitRequestSchema.parse(input.request), headers: saleskoSessionAuthHeaders() }));
}

/** Reconciles the existing durable turn only; it never creates or re-dispatches one. */
export async function reconcilePrivateAgentChatTurn(
  conversationId: string,
  turnId: string
): Promise<PrivateAgentTurnResponse> {
  return PrivateAgentTurnResponseSchema.parse(
    await agentResearchRequest<unknown>(
      `${apiBaseUrl}/api/agent/private-chat/conversations/${encodeURIComponent(conversationId)}/turns/${encodeURIComponent(turnId)}/reconcile`,
      { method: "POST", body: {}, headers: saleskoSessionAuthHeaders() }
    )
  );
}

export async function cancelPrivateAgentChatTurn(
  conversationId: string,
  turnId: string,
  input: PrivateAgentTurnCancelRequest
): Promise<PrivateAgentTurnResponse> {
  return PrivateAgentTurnResponseSchema.parse(
    await agentResearchRequest<unknown>(
      `${apiBaseUrl}/api/agent/private-chat/conversations/${encodeURIComponent(conversationId)}/turns/${encodeURIComponent(turnId)}/cancel`,
      { method: "POST", body: PrivateAgentTurnCancelRequestSchema.parse(input), headers: saleskoSessionAuthHeaders() }
    )
  );
}

/** Execute only a caller-frozen intent; this helper never selects a current task. */
export async function performPrivateAgentChatIntent(input: PrivateAgentChatClientIntent) {
  const intent = PrivateAgentChatClientIntentSchema.parse(input);
  const session = readStoredSaleskoSession();
  if (!session || session.tenantId !== intent.owner.tenantId || session.userId !== intent.owner.userId) throw new Error("Pending request owner mismatch.");
  const prefix = `${apiBaseUrl}/api/agent/private-chat/conversations/${encodeURIComponent(intent.conversationId ?? "")}`;
  const requireMatch = (matches: boolean) => { if (!matches) throw new Error("Chat action response identity mismatch."); };
  switch (intent.kind) {
    case "send": {
      const expected = intent.request.expectedContext;
      requireMatch(expected.state === "absent" ? intent.conversationId === null : intent.conversationId === expected.conversationId);
      const response = await submitPrivateAgentChatInput(intent.agentId, intent);
      requireMatch(response.clientRequestId === intent.request.clientRequestId && (expected.state === "absent"
        ? response.contextEpoch === 1 : response.conversationId === expected.conversationId && response.contextEpoch === expected.epoch));
      return response;
    }
    case "clear": {
      const response = PrivateAgentClearChatReceiptSchema.parse(await agentResearchRequest<unknown>(`${prefix}/actions`,
        { method: "POST", body: PrivateAgentClearChatRequestSchema.parse(intent.request), headers: saleskoSessionAuthHeaders() }));
      requireMatch(response.conversationId === intent.conversationId && response.clientRequestId === intent.request.clientRequestId
        && response.previousEpoch === intent.request.expectedEpoch);
      return response;
    }
    case "stop": {
      const response = await cancelPrivateAgentChatTurn(intent.conversationId, intent.turnId, intent.request);
      requireMatch(response.turn.conversationId === intent.conversationId && response.turn.turnId === intent.turnId && response.turn.contextEpoch === intent.request.expectedEpoch && (response.turn.binding === null || response.turn.binding.agentRef.agentId === intent.agentId)
        && (intent.request.execution === null ? response.turn.execution === null : response.turn.execution?.taskId === intent.request.execution.taskId && response.turn.execution.generation === intent.request.execution.generation));
      return response;
    }
    case "end": {
      const response = PrivateAgentTurnEndResponseSchema.parse(await agentResearchRequest<unknown>(`${prefix}/turns/${encodeURIComponent(intent.turnId)}/end`,
        { method: "POST", body: PrivateAgentTurnEndRequestSchema.parse(intent.request), headers: saleskoSessionAuthHeaders() }));
      requireMatch(response.turn.conversationId === intent.conversationId && response.turn.turnId === intent.turnId && response.turn.contextEpoch === intent.request.expectedEpoch && (response.turn.binding === null || response.turn.binding.agentRef.agentId === intent.agentId)
        && response.settlement.actionId === intent.request.actionId && response.settlement.taskId === intent.request.taskId && response.settlement.generation === intent.request.generation);
      return response;
    }
    case "retry": {
      const response = PrivateAgentTurnRetryResponseSchema.parse(await agentResearchRequest<unknown>(`${prefix}/turns/${encodeURIComponent(intent.turnId)}/retry`,
        { method: "POST", body: PrivateAgentTurnRetryRequestSchema.parse(intent.request), headers: saleskoSessionAuthHeaders() }));
      requireMatch(response.conversationId === intent.conversationId && response.turnId === intent.turnId && response.source.actionId === intent.request.actionId
        && response.source.taskId === intent.request.taskId && response.source.generation === intent.request.generation && response.source.expectedEpoch === intent.request.expectedEpoch);
      return response;
    }
    case "stopAndSend": {
      const response = PrivateAgentStopAndSendReceiptSchema.parse(await agentResearchRequest<unknown>(`${prefix}/stop-and-send`,
        { method: "POST", body: PrivateAgentStopAndSendRequestSchema.parse(intent.request), headers: saleskoSessionAuthHeaders() }));
      requireMatch(response.conversationId === intent.conversationId && response.actionId === intent.request.actionId && response.expectedEpoch === intent.request.expectedEpoch && response.input.clientRequestId === intent.request.input.clientRequestId
        && response.target.turnId === intent.request.target.turnId && (intent.request.target.execution === null ? response.target.execution === null
          : response.target.execution?.taskId === intent.request.target.execution.taskId && response.target.execution.generation === intent.request.target.execution.generation));
      return response;
    }
  }
}

export async function listPrivateAgentChatEvents(
  conversationId: string,
  afterSequence: number,
  epoch: number
): Promise<PrivateAgentEventReplayResponse> {
  const url = new URL(`${apiBaseUrl}/api/agent/private-chat/conversations/${encodeURIComponent(conversationId)}/events`);
  url.searchParams.set("epoch", String(epoch));
  url.searchParams.set("afterSequence", String(afterSequence));
  const replay = PrivateAgentEventReplayResponseSchema.parse(
    await agentResearchRequest<unknown>(url.toString(), { method: "GET", headers: saleskoSessionAuthHeaders() })
  );
  if (replay.conversationId !== conversationId || replay.epoch !== epoch || replay.afterSequence !== afterSequence) throw new Error("Replay snapshot identity mismatch.");
  return replay;
}

/**
 * The one server-computed answer to "can this owner chat with the Private Agent
 * right now". The browser renders this verdict; it never re-derives admission
 * from presence, device rows, or profile shape.
 */
export async function fetchPrivateAgentReadiness(agentId: string): Promise<PrivateAgentReadiness> {
  const response = PrivateAgentReadinessResponseSchema.parse(
    await agentResearchRequest<unknown>(`${apiBaseUrl}/api/agent/private-agents/${PrivateAgentAgentIdSchema.parse(agentId)}/readiness`, {
      method: "GET",
      headers: saleskoSessionAuthHeaders()
    })
  );
  if (response.readiness.agentId !== agentId) throw new Error("Private Agent readiness identity mismatch.");
  return response.readiness;
}

/**
 * Reads the readiness the API attached to a 409 `private_agent_chat_not_ready`
 * refusal. Any other failure — and any body that does not carry a readiness the
 * contract accepts — returns null rather than a synthesised verdict.
 */
export function privateAgentChatNotReadyReadiness(error: unknown): PrivateAgentReadiness | null {
  if (!(error instanceof AgentResearchApiError) || error.code !== PrivateAgentChatNotReadyErrorCode) {
    return null;
  }
  const body = error.body;
  if (!body || typeof body !== "object" || !("readiness" in body)) {
    return null;
  }
  const parsed = PrivateAgentReadinessSchema.safeParse((body as { readiness: unknown }).readiness);
  return parsed.success ? parsed.data : null;
}

export function readSaleskoSessionGrantToken(storage: SaleskoSessionGrantStorage | undefined = browserLocalStorage()): string | null {
  const session = readStoredSaleskoSession(storage);
  if (session) {
    return session.token;
  }

  if (!storage) {
    return null;
  }

  for (const key of sessionGrantStorageKeys) {
    const token = parseStoredSessionGrantToken(storage.getItem(key));
    if (token) {
      return token;
    }
  }

  return null;
}

export function readStoredSaleskoSession(storage: SaleskoSessionGrantStorage | undefined = browserLocalStorage()): StoredSaleskoSession | null {
  if (!storage) {
    return null;
  }

  const raw = storage.getItem(sessionStorageKey) ?? storage.getItem("salesko.session_grant");
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!isStoredSaleskoSession(parsed)) {
      storage.removeItem(sessionStorageKey);
      storage.removeItem("salesko.session_grant");
      return null;
    }

    if (Date.parse(parsed.expiresAt) <= Date.now()) {
      storage.removeItem(sessionStorageKey);
      storage.removeItem("salesko.session_grant");
      return null;
    }

    return parsed;
  } catch {
    storage.removeItem(sessionStorageKey);
    storage.removeItem("salesko.session_grant");
    return null;
  }
}

export function writeStoredSaleskoSession(
  session: StoredSaleskoSession,
  storage: SaleskoSessionGrantStorage | undefined = browserLocalStorage()
) {
  if (!storage) {
    return;
  }

  storage.setItem(sessionStorageKey, JSON.stringify(session));
  storage.removeItem("salesko.session_grant");
  notifyStoredSaleskoSessionChanged();
}

export function isStableBetterAuthSaleskoSession(session: StoredSaleskoSession) {
  return session.userId.startsWith(stableBetterAuthUserIdPrefix);
}

/**
 * Cache-scoping key for the connectors surface's React Query caches
 * (`["email-connections", sessionKey]` / `["email-lead-candidates", sessionKey]`
 * in ConnectorsSurface.tsx / EmailLeadCandidatesPanel.tsx). A static key would
 * let a disabled query's last `data` survive a sign-out or a switch to a
 * different session — `enabled: false` does not clear cached `data` — so a
 * different tenant's UI could render the previous session's Gmail connection
 * status/address until its own fetch resolves. Keyed on `tenantId` (this
 * product's per-user data partition; see docs/architecture/multi-tenant-saas.md)
 * rather than the whole session object so two reads of an unchanged session
 * produce an equal key. Returns null when signed out.
 */
export function emailConnectorSessionKey(session: StoredSaleskoSession | null): string | null {
  return session?.tenantId ?? null;
}

export function clearStoredSaleskoSession(storage: SaleskoSessionGrantStorage | undefined = browserLocalStorage()) {
  if (!storage) {
    return;
  }

  for (const key of sessionGrantStorageKeys) {
    storage.removeItem(key);
  }
  notifyStoredSaleskoSessionChanged();
}

export function writePendingBillingCheckoutIntent(
  productId: string,
  returnPath = "/settings",
  storage: SaleskoSessionGrantStorage | undefined = browserLocalStorage()
) {
  if (!storage) {
    return;
  }
  const normalizedProductId = productId.trim();
  const normalizedReturnPath = normalizeReturnPath(returnPath);
  if (!normalizedProductId || normalizedProductId.length > 80 || !normalizedReturnPath) {
    storage.removeItem(pendingBillingCheckoutStorageKey);
    return;
  }

  storage.setItem(
    pendingBillingCheckoutStorageKey,
    JSON.stringify({
      createdAt: new Date().toISOString(),
      productId: normalizedProductId,
      returnPath: normalizedReturnPath
    } satisfies PendingBillingCheckoutIntent)
  );
}

export function readPendingBillingCheckoutIntent(
  storage: SaleskoSessionGrantStorage | undefined = browserLocalStorage()
): PendingBillingCheckoutIntent | null {
  if (!storage) {
    return null;
  }
  const raw = storage.getItem(pendingBillingCheckoutStorageKey);
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!isPendingBillingCheckoutIntent(parsed)) {
      storage.removeItem(pendingBillingCheckoutStorageKey);
      return null;
    }
    if (Date.now() - Date.parse(parsed.createdAt) > pendingBillingCheckoutMaxAgeMs) {
      storage.removeItem(pendingBillingCheckoutStorageKey);
      return null;
    }

    return parsed;
  } catch {
    storage.removeItem(pendingBillingCheckoutStorageKey);
    return null;
  }
}

export function consumePendingBillingCheckoutIntent(
  storage: SaleskoSessionGrantStorage | undefined = browserLocalStorage()
): PendingBillingCheckoutIntent | null {
  const intent = readPendingBillingCheckoutIntent(storage);
  storage?.removeItem(pendingBillingCheckoutStorageKey);
  return intent;
}

export function consumeGoogleAuthCallbackUrl(url: string): GoogleAuthCallbackResult | null {
  const parsedUrl = new URL(url);
  const params = parsedUrl.hash ? new URLSearchParams(parsedUrl.hash.slice(1)) : parsedUrl.searchParams;
  const status = params.get("salesko_auth_status");

  if (status === "error") {
    return {
      error: params.get("error") ?? "google_auth_failed",
      status: "error"
    };
  }

  if (status !== "success") {
    return consumeSupabaseAuthCallback(params);
  }

  const token = params.get("token");
  const tokenType = params.get("token_type");
  const expiresAt = params.get("expires_at");
  const tenantId = params.get("tenant_id");
  const userId = params.get("user_id");
  if (tokenType !== "Bearer" || !token || !expiresAt || !tenantId || !userId) {
    return {
      error: "google_auth_callback_invalid",
      status: "error"
    };
  }

  const email = params.get("email");
  const name = params.get("name");

  return {
    session: {
      ...(email ? { email } : {}),
      expiresAt,
      ...(name ? { name } : {}),
      tenantId,
      token,
      tokenType,
      userId
    },
    status: "success"
  };
}

function consumeSupabaseAuthCallback(params: URLSearchParams): GoogleAuthCallbackResult | null {
  const error = params.get("error") ?? params.get("error_code");
  if (error) {
    return {
      error,
      status: "error"
    };
  }

  const token = params.get("access_token");
  const tokenType = params.get("token_type");
  if (tokenType?.toLowerCase() !== "bearer" || !token) {
    return null;
  }

  const payload = jwtPayload(token);
  const expiresAt = supabaseExpiresAt(params, payload);
  const userId = stringField(payload, "sub");
  if (!expiresAt || !userId) {
    return {
      error: "supabase_auth_callback_invalid",
      status: "error"
    };
  }

  if (!hasAllowedSupabaseAuthProvider(payload)) {
    return {
      error: "supabase_auth_provider_denied",
      status: "error"
    };
  }

  const userMetadata = recordField(payload, "user_metadata");
  const email = stringField(payload, "email");
  const name = stringField(userMetadata, "name") ?? stringField(userMetadata, "full_name") ?? email;
  return {
    session: {
      ...(email ? { email } : {}),
      expiresAt,
      ...(name ? { name } : {}),
      tenantId: saleskoTenantId,
      token,
      tokenType: "Bearer",
      userId
    },
    status: "success"
  };
}

export async function commitGraphFrame(input: GraphFrameWrite): Promise<GraphFrameCommitResponse> {
  return postJson<GraphFrameCommitResponse>(`${apiBaseUrl}/api/graph/frames`, input, saleskoSessionAuthHeaders());
}

export async function createNoteIntake(input: GraphNoteIntakeInput): Promise<GraphNoteIntakeResponse> {
  return postJson<GraphNoteIntakeResponse>(`${apiBaseUrl}/v1/notes`, input, saleskoSessionAuthHeaders());
}

export async function extractTransactionProposals(
  input: TransactionProposalExtractionRequest
): Promise<TransactionProposalExtractionResponse> {
  const request = TransactionProposalExtractionRequestSchema.parse(input);
  const response = await postJson<unknown>(
    `${agentBaseUrl}/api/agent/transactions/proposals`,
    request,
    await agentProviderAuthHeaders()
  );
  return TransactionProposalExtractionResponseSchema.parse(response);
}

export async function listActivityFeed(input: Partial<ActivityFeedQuery> = {}): Promise<ActivityFeedResponse> {
  const query = ActivityFeedQuerySchema.parse(input);
  const url = new URL(`${apiBaseUrl}/v1/activity-feed`);
  if (query.cursor) {
    url.searchParams.set("cursor", query.cursor);
  }
  url.searchParams.set("limit", String(query.limit));
  return ActivityFeedResponseSchema.parse(await getJson<unknown>(url.toString(), saleskoSessionAuthHeaders()));
}

export async function getTransactionActivityCapability(): Promise<TransactionActivityCapabilityResponse> {
  return TransactionActivityCapabilityResponseSchema.parse(
    await getJson<unknown>(`${apiBaseUrl}/v1/transaction-activity/capability`, saleskoSessionAuthHeaders())
  );
}

export async function getTransactionDetail(canonicalTransactionId: string): Promise<TransactionDetailResponse> {
  return TransactionDetailResponseSchema.parse(
    await getJson<unknown>(
      `${apiBaseUrl}/v1/transactions/${encodeURIComponent(canonicalTransactionId)}`,
      saleskoSessionAuthHeaders()
    )
  );
}

export async function getTransactionGraph(canonicalTransactionId: string): Promise<TransactionGraphResponse> {
  return TransactionGraphResponseSchema.parse(
    await getJson<unknown>(
      `${apiBaseUrl}/v1/transactions/${encodeURIComponent(canonicalTransactionId)}/graph`,
      saleskoSessionAuthHeaders()
    )
  );
}

export async function getActivitySourceDetail(
  entryId: string,
  sourceReferenceHash: string
): Promise<ActivitySourceDetailResponse> {
  return ActivitySourceDetailResponseSchema.parse(
    await getJson<unknown>(
      `${apiBaseUrl}/v1/activity-feed/${encodeURIComponent(entryId)}/sources/${encodeURIComponent(sourceReferenceHash)}`,
      saleskoSessionAuthHeaders()
    )
  );
}

export async function recordTransaction(input: TransactionRecordCommand): Promise<TransactionCommandResponse> {
  const command = TransactionRecordCommandSchema.parse(input);
  return TransactionCommandResponseSchema.parse(
    await postJson<unknown>(`${apiBaseUrl}/v1/transactions`, command, saleskoSessionAuthHeaders())
  );
}

export async function changeTransactionStatus(
  canonicalTransactionId: string,
  input: TransactionStatusChangeCommand
): Promise<TransactionCommandResponse> {
  const command = TransactionStatusChangeCommandSchema.parse(input);
  return TransactionCommandResponseSchema.parse(
    await postJson<unknown>(
      `${apiBaseUrl}/v1/transactions/${encodeURIComponent(canonicalTransactionId)}/status-changes`,
      command,
      saleskoSessionAuthHeaders()
    )
  );
}

export async function correctTransaction(
  canonicalTransactionId: string,
  input: TransactionCorrectionCommand
): Promise<TransactionCommandResponse> {
  const command = TransactionCorrectionCommandSchema.parse(input);
  return TransactionCommandResponseSchema.parse(
    await postJson<unknown>(
      `${apiBaseUrl}/v1/transactions/${encodeURIComponent(canonicalTransactionId)}/corrections`,
      command,
      saleskoSessionAuthHeaders()
    )
  );
}

export async function backfillActivity(input: ActivityBackfillCommand): Promise<ActivityBackfillResponse> {
  const command = ActivityBackfillCommandSchema.parse(input);
  return ActivityBackfillResponseSchema.parse(
    await postJson<unknown>(`${apiBaseUrl}/v1/activity-feed/backfill`, command, saleskoSessionAuthHeaders())
  );
}

export async function getNoteIntakeJob(intakeId: string): Promise<GraphNoteIntakeJobRecord> {
  return getJson<GraphNoteIntakeJobRecord>(
    `${apiBaseUrl}/v1/notes/${encodeURIComponent(intakeId)}`,
    saleskoSessionAuthHeaders()
  );
}

export async function importJsonGraphFrame(input: GraphFrameJsonImportRequest): Promise<GraphFrameImportResponse> {
  return postJson<GraphFrameImportResponse>(
    `${apiBaseUrl}/api/graph/frames/imports/json`,
    input,
    saleskoSessionAuthHeaders()
  );
}

export async function importObsidianGraphFrame(
  input: GraphFrameObsidianImportRequest
): Promise<GraphFrameImportResponse> {
  return postJson<GraphFrameImportResponse>(
    `${apiBaseUrl}/api/graph/frames/imports/obsidian`,
    input,
    saleskoSessionAuthHeaders()
  );
}

export async function importCsvGraphChangeSet(
  frameRecordId: string,
  input: GraphCsvImportRequest
): Promise<GraphCsvImportResponse> {
  return postJson<GraphCsvImportResponse>(
    `${apiBaseUrl}/api/graph/frames/${encodeURIComponent(frameRecordId)}/imports/csv`,
    input,
    saleskoSessionAuthHeaders()
  );
}

export async function createGraphChangeSet(
  frameRecordId: string,
  input: GraphChangeSetWrite
): Promise<GraphChangeSetCreateResponse> {
  return postJson<GraphChangeSetCreateResponse>(
    `${apiBaseUrl}/api/graph/frames/${encodeURIComponent(frameRecordId)}/change-sets`,
    input,
    saleskoSessionAuthHeaders()
  );
}

export async function getAgentResearchEntitlement(): Promise<AgentResearchEntitlementResponse> {
  return agentResearchRequest<AgentResearchEntitlementResponse>(
    `${apiBaseUrl}/api/agent/research-entitlement`,
    { method: "GET", headers: saleskoSessionAuthHeaders() }
  );
}

/**
 * Private Agent research jobs. Every one of these routes answers non-2xx with a
 * typed `{ error: { code } }` body that is itself a product state — no active
 * computer, no Private Agent profile, an active job already running, a stale
 * frame version — so they all go through `agentResearchRequest` and surface
 * `AgentResearchApiError.code` instead of message text. Responses are parsed
 * with the contract schemas: a shape the contract does not describe is a
 * failure, not something to render defensively around.
 */
export async function listAgentResearchJobs(agentId: string, limit = 20): Promise<AgentResearchJobListResponse> {
  const url = new URL(`${apiBaseUrl}/api/agent/research-jobs`);
  url.searchParams.set("agentId", PrivateAgentAgentIdSchema.parse(agentId));
  url.searchParams.set("limit", String(limit));
  return AgentResearchJobListResponseSchema.parse(
    await agentResearchRequest<unknown>(url.toString(), { method: "GET", headers: saleskoSessionAuthHeaders() })
  );
}

export async function getAgentResearchJob(jobId: string): Promise<AgentResearchJobReadResponse> {
  return AgentResearchJobReadResponseSchema.parse(
    await agentResearchRequest<unknown>(`${apiBaseUrl}/api/agent/research-jobs/${encodeURIComponent(jobId)}`, {
      method: "GET",
      headers: saleskoSessionAuthHeaders()
    })
  );
}

export async function createAgentResearchJob(
  input: AgentResearchJobCreateRequest
): Promise<AgentResearchJobCreateResponse> {
  return AgentResearchJobCreateResponseSchema.parse(
    await agentResearchRequest<unknown>(`${apiBaseUrl}/api/agent/research-jobs`, {
      method: "POST",
      body: AgentResearchJobCreateRequestSchema.parse(input),
      headers: saleskoSessionAuthHeaders()
    })
  );
}

export async function cancelAgentResearchJob(jobId: string): Promise<AgentResearchJobCancelResponse> {
  return AgentResearchJobCancelResponseSchema.parse(
    await agentResearchRequest<unknown>(
      `${apiBaseUrl}/api/agent/research-jobs/${encodeURIComponent(jobId)}/cancel`,
      { method: "POST", body: {}, headers: saleskoSessionAuthHeaders() }
    )
  );
}

export async function materializeAgentResearchChangeSet(
  jobId: string
): Promise<AgentResearchMaterializeChangeSetResponse> {
  return AgentResearchMaterializeChangeSetResponseSchema.parse(
    await agentResearchRequest<unknown>(
      `${apiBaseUrl}/api/agent/research-jobs/${encodeURIComponent(jobId)}/materialize-change-set`,
      { method: "POST", body: {}, headers: saleskoSessionAuthHeaders() }
    )
  );
}

/** Fetches the Gmail consent URL; the caller does the top-level redirect
 * itself (`window.location.href = url`) rather than navigating straight to
 * this endpoint — the session grant lives in localStorage, not a cookie. */
export function emailProviderFromConnectorId(connectorId: string): EmailProvider | null {
  if (connectorId === "gmail") {
    return "gmail";
  }
  if (connectorId === "microsoft-email") {
    return "microsoft";
  }
  return null;
}

function emailConnectorProviderBasePath(provider: EmailProvider): string {
  return provider === "gmail" ? "/api/connectors/gmail" : "/api/connectors/email/microsoft";
}

export async function getEmailConnectorAuthorizeUrl(
  provider: EmailProvider
): Promise<EmailConnectorAuthorizeUrlResponse> {
  return postJson<EmailConnectorAuthorizeUrlResponse>(
    `${apiBaseUrl}${emailConnectorProviderBasePath(provider)}/authorize-url`,
    {},
    saleskoSessionAuthHeaders(),
    { credentials: "include" }
  );
}

export async function getEmailConnections(): Promise<EmailConnectionStatusResponse> {
  return getJson<EmailConnectionStatusResponse>(`${apiBaseUrl}/api/connectors/email/status`, saleskoSessionAuthHeaders());
}

export async function disconnectEmail(provider: EmailProvider): Promise<void> {
  await postJson<{ ok: boolean }>(
    `${apiBaseUrl}${emailConnectorProviderBasePath(provider)}/disconnect`,
    {},
    saleskoSessionAuthHeaders()
  );
}

export async function listEmailReplyTemplates(): Promise<EmailReplyTemplateListResponse> {
  return getJson<EmailReplyTemplateListResponse>(`${apiBaseUrl}/api/connectors/email/reply-templates`, saleskoSessionAuthHeaders());
}

export async function createEmailReplyTemplate(input: EmailReplyTemplateCreateRequest): Promise<EmailReplyTemplateResponse> {
  return postJson<EmailReplyTemplateResponse>(
    `${apiBaseUrl}/api/connectors/email/reply-templates`,
    input,
    saleskoSessionAuthHeaders()
  );
}

export async function updateEmailReplyTemplate(id: string, input: EmailReplyTemplateUpdateRequest): Promise<EmailReplyTemplateResponse> {
  return putJson<EmailReplyTemplateResponse>(
    `${apiBaseUrl}/api/connectors/email/reply-templates/${encodeURIComponent(id)}`,
    input,
    saleskoSessionAuthHeaders()
  );
}

/** Manually triggers a Gmail sync. `transport: "cloudflare_queue"` means the
 * sync was enqueued and runs asynchronously (the caller should refetch
 * connections/candidates after a short delay); `"inline"` means it already
 * ran by the time this resolves. */
export async function syncEmailNow(provider: EmailProvider): Promise<EmailSyncTriggerResponse> {
  return postJson<EmailSyncTriggerResponse>(
    `${apiBaseUrl}${emailConnectorProviderBasePath(provider)}/sync`,
    {},
    saleskoSessionAuthHeaders()
  );
}

// --- Calendar connector (Google + Microsoft). Mirrors the gmail functions
// above; the provider is a path param (:provider = google|microsoft) rather
// than a hard-coded /gmail segment because calendar ships two real providers.
// The sync-trigger shape is identical to EmailSyncTriggerResponse; the schema
// is inlined in apps/api (Slice A/B/C contracts are frozen), so a local type
// avoids importing the email-named contract for calendar semantics.
export type CalendarSyncTriggerResponse = {
  status: "queued" | "completed";
  connectionId: string;
  transport: "cloudflare_queue" | "inline";
};

export function calendarProviderFromConnectorId(connectorId: string): CalendarProvider | null {
  if (connectorId === "google-calendar") {
    return "google";
  }
  if (connectorId === "microsoft-calendar") {
    return "microsoft";
  }
  return null;
}

export async function getCalendarConnectorAuthorizeUrl(provider: CalendarProvider): Promise<CalendarConnectorAuthorizeUrlResponse> {
  return postJson<CalendarConnectorAuthorizeUrlResponse>(
    `${apiBaseUrl}/api/connectors/calendar/${encodeURIComponent(provider)}/authorize-url`,
    {},
    saleskoSessionAuthHeaders(),
    { credentials: "include" }
  );
}

export async function getCalendarConnections(): Promise<CalendarConnectionStatusResponse> {
  return getJson<CalendarConnectionStatusResponse>(`${apiBaseUrl}/api/connectors/calendar/status`, saleskoSessionAuthHeaders());
}

export async function disconnectCalendar(provider: CalendarProvider): Promise<void> {
  await postJson<{ ok: boolean }>(
    `${apiBaseUrl}/api/connectors/calendar/${encodeURIComponent(provider)}/disconnect`,
    {},
    saleskoSessionAuthHeaders()
  );
}

export async function syncCalendarNow(provider: CalendarProvider): Promise<CalendarSyncTriggerResponse> {
  return postJson<CalendarSyncTriggerResponse>(
    `${apiBaseUrl}/api/connectors/calendar/${encodeURIComponent(provider)}/sync`,
    {},
    saleskoSessionAuthHeaders()
  );
}

export async function dismissCalendarEvent(
  providerEventId: string,
  input: { provider?: CalendarProvider; clientMutationId?: string } = {}
): Promise<CalendarEventDismissResponse> {
  const query = input.provider ? `?provider=${encodeURIComponent(input.provider)}` : "";
  return postJson<CalendarEventDismissResponse>(
    `${apiBaseUrl}/api/connectors/calendar/events/${encodeURIComponent(providerEventId)}/dismiss${query}`,
    input.clientMutationId !== undefined ? { clientMutationId: input.clientMutationId } : {},
    saleskoSessionAuthHeaders()
  );
}

/**
 * Thrown by prepareMeetingBrief on a structured 4xx/5xx response. Preserves the
 * stable `meeting_preparation_*` code (from MeetingPreparationErrorResponseSchema)
 * so callers can branch on it without re-deriving eligibility/entitlement/model
 * semantics locally — mirrors AgentResearchApiError's code-preserving throw above.
 */
export class MeetingPreparationApiError extends Error {
  readonly code: MeetingPreparationErrorCode;

  constructor(code: MeetingPreparationErrorCode, message: string) {
    super(message);
    this.name = "MeetingPreparationApiError";
    this.code = code;
  }
}

/**
 * Requests an on-demand meeting-preparation brief for a single Calendar-sourced
 * event (SNW-06's explicit "Prepare" command in DetailPanel). The request body is
 * exactly {provider, providerEventId} per MeetingPreparationRequestSchema — no
 * client-computed eligibility, context, or brief content crosses this call.
 *
 * The success body is parsed through MeetingPreparationResponseSchema. A non-ok
 * response's body is parsed through MeetingPreparationErrorResponseSchema and
 * re-thrown as MeetingPreparationApiError so DetailPanel's mutation can branch on
 * the stable code. Unlike agentResearchRequest's error path above, this does not
 * fall back to a null code on a body that fails that schema — a response that
 * satisfies neither schema is a genuine contract violation (bad gateway, corrupted
 * payload), and is allowed to throw the underlying parse failure rather than
 * inventing a fallback code for it.
 */
export async function prepareMeetingBrief(
  input: MeetingPreparationRequest
): Promise<MeetingPreparationResponse> {
  const headers = new Headers(saleskoSessionAuthHeaders());
  headers.set("Content-Type", "application/json");

  const response = await fetch(`${apiBaseUrl}/v1/meeting-preparations`, {
    method: "POST",
    headers,
    body: JSON.stringify(input)
  });

  if (!response.ok) {
    const errorBody = MeetingPreparationErrorResponseSchema.parse(await response.json());
    throw new MeetingPreparationApiError(errorBody.error.code, errorBody.error.message);
  }

  return MeetingPreparationResponseSchema.parse(await response.json());
}

export async function listEmailLeadCandidates(): Promise<EmailLeadCandidateListResponse> {
  return getJson<EmailLeadCandidateListResponse>(`${apiBaseUrl}/api/connectors/email/candidates`, saleskoSessionAuthHeaders());
}

export async function confirmEmailLeadCandidate(
  id: string,
  input: EmailLeadCandidateConfirmRequest
): Promise<EmailLeadCandidateConfirmResponse> {
  return postJson<EmailLeadCandidateConfirmResponse>(
    `${apiBaseUrl}/api/connectors/email/candidates/${encodeURIComponent(id)}/confirm`,
    input,
    saleskoSessionAuthHeaders()
  );
}

export async function createEmailReplyDraft(
  id: string,
  input: EmailReplyDraftCreateRequest = {}
): Promise<EmailReplyDraftCreateResponse> {
  return postJson<EmailReplyDraftCreateResponse>(
    `${apiBaseUrl}/api/connectors/email/candidates/${encodeURIComponent(id)}/reply-draft`,
    input,
    saleskoSessionAuthHeaders()
  );
}

export async function dismissEmailLeadCandidate(id: string): Promise<EmailLeadCandidateDismissResponse> {
  return postJson<EmailLeadCandidateDismissResponse>(
    `${apiBaseUrl}/api/connectors/email/candidates/${encodeURIComponent(id)}/dismiss`,
    {},
    saleskoSessionAuthHeaders()
  );
}

export async function getBillingEntitlement(): Promise<SaleskoBillingEntitlementResponse> {
  return getJson<SaleskoBillingEntitlementResponse>(
    `${apiBaseUrl}/api/billing/subscription`,
    saleskoSessionAuthHeaders()
  );
}

export async function getBillingCatalog(): Promise<SaleskoBillingCatalogResponse> {
  return getJson<SaleskoBillingCatalogResponse>(`${apiBaseUrl}/api/billing/catalog`, saleskoSessionAuthHeaders());
}

// Catalog-driven checkout for a subscription tier or one-time credit pack.
export async function startCheckout(
  productId: string,
  returnPath?: string
): Promise<SaleskoBillingCheckoutResponse> {
  return postJson<SaleskoBillingCheckoutResponse>(
    `${apiBaseUrl}/api/billing/checkout`,
    { productId, ...(returnPath ? { returnPath } : {}) },
    saleskoSessionAuthHeaders()
  );
}

export async function getCreditBalance(): Promise<SaleskoBillingCreditBalanceResponse> {
  return getJson<SaleskoBillingCreditBalanceResponse>(
    `${apiBaseUrl}/api/billing/credits`,
    saleskoSessionAuthHeaders()
  );
}

export async function getCreditHistory(): Promise<SaleskoBillingCreditHistoryResponse> {
  return getJson<SaleskoBillingCreditHistoryResponse>(
    `${apiBaseUrl}/api/billing/credits/history`,
    saleskoSessionAuthHeaders()
  );
}

export async function createPersonalAgentPairingCode(): Promise<PersonalAgentPairingCodeResponse> {
  return postJson<PersonalAgentPairingCodeResponse>(
    `${apiBaseUrl}/api/agent/devices/pairing-codes`,
    {},
    saleskoSessionAuthHeaders()
  );
}

export async function listPersonalAgentDevices(): Promise<PersonalAgentDeviceListResponse> {
  return getJson<PersonalAgentDeviceListResponse>(`${apiBaseUrl}/api/agent/devices`, saleskoSessionAuthHeaders());
}

export async function listPersonalAgentPresenceHints(): Promise<PersonalAgentPresenceHintListResponse> {
  return getJson<PersonalAgentPresenceHintListResponse>(
    `${apiBaseUrl}/api/agent/devices/presence`,
    saleskoSessionAuthHeaders()
  );
}

export async function revokePersonalAgentDevice(deviceId: string): Promise<PersonalAgentDeviceRevokeResponse> {
  return postJson<PersonalAgentDeviceRevokeResponse>(
    `${apiBaseUrl}/api/agent/devices/${encodeURIComponent(deviceId)}/revoke`,
    {},
    saleskoSessionAuthHeaders()
  );
}

/**
 * Private Agent profile CRUD. A 404 (`private_agent_profile_not_found`) is a
 * real state the settings surface renders, not a transport failure, so these
 * throw the typed `AgentResearchApiError` and let the caller branch on status.
 */
export async function listPrivateAgents(): Promise<PrivateAgentListResponse> {
  return PrivateAgentListResponseSchema.parse(await agentResearchRequest<unknown>(`${apiBaseUrl}/api/agent/private-agents`, {
    method: "GET", headers: saleskoSessionAuthHeaders()
  }));
}

export async function createPrivateAgent(input: PrivateAgentCreateRequest): Promise<PrivateAgentProfileResponsePhase3> {
  return PrivateAgentProfileResponsePhase3Schema.parse(await agentResearchRequest<unknown>(`${apiBaseUrl}/api/agent/private-agents`, {
    method: "POST", body: PrivateAgentCreateRequestSchema.parse(input), headers: saleskoSessionAuthHeaders()
  }));
}

export async function getPrivateAgentProfile(agentId: string): Promise<PrivateAgentProfileResponsePhase3> {
  return PrivateAgentProfileResponsePhase3Schema.parse(await agentResearchRequest<unknown>(`${apiBaseUrl}/api/agent/private-agents/${PrivateAgentAgentIdSchema.parse(agentId)}`, {
    method: "GET",
    headers: saleskoSessionAuthHeaders()
  }));
}

export async function savePrivateAgentProfile(agentId: string, input: PrivateAgentProfileWrite): Promise<PrivateAgentProfileResponsePhase3> {
  return PrivateAgentProfileResponsePhase3Schema.parse(await agentResearchRequest<unknown>(`${apiBaseUrl}/api/agent/private-agents/${PrivateAgentAgentIdSchema.parse(agentId)}/profile`, {
    method: "PUT",
    body: PrivateAgentProfileWriteSchema.parse(input),
    headers: saleskoSessionAuthHeaders()
  }));
}

export async function updatePrivateAgentPlacement(agentId: string, input: PrivateAgentPlacementUpdateRequest): Promise<PrivateAgentProfileResponsePhase3> {
  return PrivateAgentProfileResponsePhase3Schema.parse(await agentResearchRequest<unknown>(`${apiBaseUrl}/api/agent/private-agents/${PrivateAgentAgentIdSchema.parse(agentId)}/placement`, {
    method: "PUT", body: PrivateAgentPlacementUpdateRequestSchema.parse(input), headers: saleskoSessionAuthHeaders()
  }));
}

export async function deletePrivateAgentProfile(agentId: string): Promise<void> {
  await agentResearchRequest<undefined>(`${apiBaseUrl}/api/agent/private-agents/${PrivateAgentAgentIdSchema.parse(agentId)}`, {
    method: "DELETE",
    headers: saleskoSessionAuthHeaders()
  });
}

export async function mergeCanonicalEntity(
  sourceCanonicalEntityId: string,
  input: GraphCanonicalEntityMergeRequest
): Promise<GraphCanonicalEntityMergeResponse> {
  return postJson<GraphCanonicalEntityMergeResponse>(
    `${apiBaseUrl}/api/graph/canonical-entities/${encodeURIComponent(sourceCanonicalEntityId)}/merge`,
    input,
    saleskoSessionAuthHeaders()
  );
}

export async function rekeyCanonicalEntity(
  sourceCanonicalEntityId: string,
  input: GraphCanonicalEntityRekeyRequest
): Promise<GraphCanonicalEntityRekeyResponse> {
  return postJson<GraphCanonicalEntityRekeyResponse>(
    `${apiBaseUrl}/api/graph/canonical-entities/${encodeURIComponent(sourceCanonicalEntityId)}/rekey`,
    input,
    saleskoSessionAuthHeaders()
  );
}

export async function splitCanonicalEntity(
  sourceCanonicalEntityId: string,
  input: GraphCanonicalEntitySplitRequest
): Promise<GraphCanonicalEntitySplitResponse> {
  return postJson<GraphCanonicalEntitySplitResponse>(
    `${apiBaseUrl}/api/graph/canonical-entities/${encodeURIComponent(sourceCanonicalEntityId)}/split`,
    input,
    saleskoSessionAuthHeaders()
  );
}

export async function listCanonicalEntities(input: {
  entityType?: GraphEntityType;
  limit?: number;
  query?: string;
} = {}): Promise<GraphCanonicalEntityListResponse> {
  const url = new URL(`${apiBaseUrl}/api/graph/canonical-entities`);
  const query = input.query?.trim();
  if (query) {
    url.searchParams.set("query", query);
  }
  if (input.entityType) {
    url.searchParams.set("entityType", input.entityType);
  }
  if (input.limit !== undefined) {
    url.searchParams.set("limit", String(input.limit));
  }

  return getJson<GraphCanonicalEntityListResponse>(url.toString(), saleskoSessionAuthHeaders());
}

export async function getCanonicalEntity(
  canonicalEntityId: string,
  input: { limit?: number } = {}
): Promise<GraphCanonicalEntityDetailResponse> {
  const url = new URL(`${apiBaseUrl}/api/graph/canonical-entities/${encodeURIComponent(canonicalEntityId)}`);
  if (input.limit !== undefined) {
    url.searchParams.set("limit", String(input.limit));
  }

  return getJson<GraphCanonicalEntityDetailResponse>(url.toString(), saleskoSessionAuthHeaders());
}

export async function listGraphFrames(limit = 8): Promise<GraphFrameListResponse> {
  const url = new URL(`${apiBaseUrl}/api/graph/frames`);
  url.searchParams.set("limit", String(limit));
  return getJson<GraphFrameListResponse>(url.toString(), saleskoSessionAuthHeaders());
}

export async function getGraphFrame(frameRecordId: string): Promise<GraphFrameRecord> {
  return getJson<GraphFrameRecord>(
    `${apiBaseUrl}/api/graph/frames/${encodeURIComponent(frameRecordId)}`,
    saleskoSessionAuthHeaders()
  );
}

// Persist the ego-center for the signed-in user. Pass null to clear. Best-effort from
// the workspace (localStorage stays the optimistic source); frame reads resolve it.
export async function setServerSelfNode(selfCanonicalEntityId: string | null): Promise<void> {
  await putJson<{ ok: boolean }>(
    `${apiBaseUrl}/api/graph/self-node`,
    { selfCanonicalEntityId },
    saleskoSessionAuthHeaders()
  );
}

export async function searchGraphNodes(input: {
  entityType?: GraphEntityType;
  limit?: number;
  query: string;
}): Promise<GraphSearchResponse> {
  const url = new URL(`${apiBaseUrl}/api/graph/nodes`);
  const query = input.query.trim();
  if (query) {
    url.searchParams.set("query", query);
  }
  if (input.entityType) {
    url.searchParams.set("entityType", input.entityType);
  }
  if (input.limit) {
    url.searchParams.set("limit", String(input.limit));
  }

  return getJson<GraphSearchResponse>(url.toString(), saleskoSessionAuthHeaders());
}

export async function getGraphOverviewProjection(): Promise<GraphProjectionResponse> {
  return getJson<GraphProjectionResponse>(
    `${apiBaseUrl}/api/graph/overview/projection`,
    saleskoSessionAuthHeaders()
  );
}

export async function getGraphRelationshipPaths(
  sourceNodeId: string,
  targetNodeId: string,
  input: { limit?: number } = {}
): Promise<GraphRelationshipPathsResponse> {
  const url = new URL(
    `${apiBaseUrl}/api/graph/nodes/${encodeURIComponent(sourceNodeId)}/paths/${encodeURIComponent(targetNodeId)}`
  );
  if (input.limit) {
    url.searchParams.set("limit", String(input.limit));
  }

  return getJson<GraphRelationshipPathsResponse>(url.toString(), saleskoSessionAuthHeaders());
}

export async function listGraphRelationshipReminders(input: {
  asOf?: string;
  limit?: number;
  staleAfterDays?: number;
} = {}): Promise<GraphRelationshipRemindersResponse> {
  const url = new URL(`${apiBaseUrl}/api/graph/relationships/reminders`);
  if (input.staleAfterDays) {
    url.searchParams.set("staleAfterDays", String(input.staleAfterDays));
  }
  if (input.asOf) {
    url.searchParams.set("asOf", input.asOf);
  }
  if (input.limit) {
    url.searchParams.set("limit", String(input.limit));
  }

  return getJson<GraphRelationshipRemindersResponse>(url.toString(), saleskoSessionAuthHeaders());
}

export async function getSaleskoSchedule(input: { from: string; to: string }): Promise<SaleskoScheduleResponse> {
  const url = new URL(`${apiBaseUrl}/v1/schedule`);
  url.searchParams.set("from", input.from);
  url.searchParams.set("to", input.to);
  return getJson<SaleskoScheduleResponse>(url.toString(), saleskoSessionAuthHeaders());
}

export async function listGraphOpportunities(input: {
  limit?: number;
  query?: string;
  status?: GraphOpportunityStatus;
} = {}): Promise<GraphOpportunityFeedResponse> {
  const url = new URL(`${apiBaseUrl}/v1/opportunities`);
  const query = input.query?.trim();
  if (query) {
    url.searchParams.set("query", query);
  }
  if (input.status) {
    url.searchParams.set("status", input.status);
  }
  if (input.limit) {
    url.searchParams.set("limit", String(input.limit));
  }

  return getJson<GraphOpportunityFeedResponse>(url.toString(), saleskoSessionAuthHeaders());
}

export async function updateGraphOpportunity(
  opportunityId: string,
  input: GraphOpportunityActionRequest
): Promise<GraphOpportunityActionResponse> {
  return postJson<GraphOpportunityActionResponse>(
    `${apiBaseUrl}/v1/opportunities/${encodeURIComponent(opportunityId)}/actions`,
    input,
    saleskoSessionAuthHeaders()
  );
}

export async function createGraphFrameShareLink(
  frameRecordId: string,
  input: GraphFrameShareLinkIssueRequest = {}
): Promise<GraphFrameShareLinkIssueResponse> {
  return postJson<GraphFrameShareLinkIssueResponse>(
    `${apiBaseUrl}/api/graph/frames/${encodeURIComponent(frameRecordId)}/share-links`,
    input,
    saleskoSessionAuthHeaders()
  );
}

export async function readSharedGraphFrame(token: string): Promise<GraphFrameShareReadResponse> {
  return getJson<GraphFrameShareReadResponse>(`${apiBaseUrl}/api/graph/shared/${encodeURIComponent(token)}`);
}

export async function getTenantPolicy(): Promise<SaleskoTenantPolicyResponse> {
  return getJson<SaleskoTenantPolicyResponse>(`${apiBaseUrl}/api/tenant/policy`, saleskoSessionAuthHeaders());
}

export async function updateTenantPolicy(input: SaleskoTenantPolicyUpdateRequest): Promise<SaleskoTenantPolicyResponse> {
  return putJson<SaleskoTenantPolicyResponse>(`${apiBaseUrl}/api/tenant/policy`, input, saleskoSessionAuthHeaders());
}

export async function getTenantAgentSettings(): Promise<SaleskoAgentSettingsResponse> {
  return getJson<SaleskoAgentSettingsResponse>(`${apiBaseUrl}/api/tenant/agent-settings`, saleskoSessionAuthHeaders());
}

export async function updateTenantAgentSettings(input: SaleskoAgentSettings): Promise<SaleskoAgentSettingsResponse> {
  return putJson<SaleskoAgentSettingsResponse>(
    `${apiBaseUrl}/api/tenant/agent-settings`,
    input,
    saleskoSessionAuthHeaders()
  );
}

export async function listGraphChangeSets(frameRecordId: string, limit = 20): Promise<GraphChangeSetListResponse> {
  const url = new URL(`${apiBaseUrl}/api/graph/frames/${encodeURIComponent(frameRecordId)}/change-sets`);
  url.searchParams.set("limit", String(limit));
  return getJson<GraphChangeSetListResponse>(url.toString(), saleskoSessionAuthHeaders());
}

export async function listGraphChangeSetAuditLogs(
  frameRecordId: string,
  changeSetId: string,
  limit = 20
): Promise<GraphChangeSetAuditLogListResponse> {
  const url = new URL(
    `${apiBaseUrl}/api/graph/frames/${encodeURIComponent(frameRecordId)}/change-sets/${encodeURIComponent(changeSetId)}/audit-log`
  );
  url.searchParams.set("limit", String(limit));
  return getJson<GraphChangeSetAuditLogListResponse>(url.toString(), saleskoSessionAuthHeaders());
}

export async function exportGraphFrame(
  frameRecordId: string,
  format: GraphFrameExportFormat = "json"
): Promise<GraphFrameExportResponse> {
  const url = new URL(`${apiBaseUrl}/api/graph/frames/${encodeURIComponent(frameRecordId)}/export`);
  url.searchParams.set("format", format);
  return getJson<GraphFrameExportResponse>(url.toString(), saleskoSessionAuthHeaders());
}

export async function exportTenantPrivacyZip(): Promise<BinaryArtifactDownload> {
  const url = new URL(`${apiBaseUrl}/api/graph/privacy/export`);
  url.searchParams.set("format", "zip");
  const headers = saleskoSessionAuthHeaders();
  const response = await fetch(url.toString(), headers ? { headers } : undefined);

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Request failed with ${response.status}`);
  }

  const contentType = response.headers.get("content-type") ?? "application/zip";
  return {
    blob: await response.blob(),
    contentType,
    filename: filenameFromContentDisposition(response.headers.get("content-disposition")) ?? "salesko-privacy-export.zip"
  };
}

export async function exportGraphChangeSetAudit(
  frameRecordId: string,
  changeSetId: string
): Promise<GraphChangeSetAuditExportResponse> {
  return getJson<GraphChangeSetAuditExportResponse>(
    `${apiBaseUrl}/api/graph/frames/${encodeURIComponent(frameRecordId)}/change-sets/${encodeURIComponent(changeSetId)}/audit-export`,
    saleskoSessionAuthHeaders()
  );
}

export async function applyGraphChangeSet(changeSetId: string): Promise<GraphChangeSetApplyResponse> {
  return postJson<GraphChangeSetApplyResponse>(
    `${apiBaseUrl}/api/graph/change-sets/${encodeURIComponent(changeSetId)}/apply`,
    {},
    saleskoSessionAuthHeaders()
  );
}

export async function revertGraphChangeSet(changeSetId: string): Promise<GraphChangeSetRevertResponse> {
  return postJson<GraphChangeSetRevertResponse>(
    `${apiBaseUrl}/api/graph/change-sets/${encodeURIComponent(changeSetId)}/revert`,
    {},
    saleskoSessionAuthHeaders()
  );
}

async function postJson<TResponse>(
  url: string,
  body: unknown,
  requestHeaders?: HeadersInit,
  options?: { credentials?: RequestCredentials }
): Promise<TResponse> {
  return writeJson<TResponse>(url, "POST", body, requestHeaders, options);
}

async function putJson<TResponse>(url: string, body: unknown, requestHeaders?: HeadersInit): Promise<TResponse> {
  return writeJson<TResponse>(url, "PUT", body, requestHeaders);
}

async function writeJson<TResponse>(
  url: string,
  method: "DELETE" | "POST" | "PUT",
  body: unknown,
  requestHeaders?: HeadersInit,
  options?: { credentials?: RequestCredentials }
): Promise<TResponse> {
  const headers = new Headers(requestHeaders);
  headers.set("Content-Type", "application/json");

  const response = await fetch(url, {
    method,
    headers,
    body: JSON.stringify(body),
    ...(options?.credentials ? { credentials: options.credentials } : {})
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Request failed with ${response.status}`);
  }

  return response.json() as Promise<TResponse>;
}

async function deleteJson<TResponse>(url: string, requestHeaders?: HeadersInit, body?: unknown): Promise<TResponse> {
  if (body !== undefined) {
    return writeJson<TResponse>(url, "DELETE", body, requestHeaders);
  }

  const response = await fetch(url, {
    method: "DELETE",
    ...(requestHeaders ? { headers: requestHeaders } : {})
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Request failed with ${response.status}`);
  }

  return response.json() as Promise<TResponse>;
}

async function getJson<TResponse>(url: string, requestHeaders?: HeadersInit): Promise<TResponse> {
  const response = await fetch(url, requestHeaders ? { headers: requestHeaders } : undefined);

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Request failed with ${response.status}`);
  }

  return response.json() as Promise<TResponse>;
}

/**
 * Error thrown by the helpers whose non-2xx responses are themselves product
 * states: `getAgentResearchEntitlement` (the shared paid-tier/Private Agent
 * access gate behind Connectors, Credits, Computers, and TenantAdmin) and the
 * Private Agent profile CRUD (404 = no agent yet, 409 = the bound computer is
 * not active). Unlike the generic `Error` thrown by `writeJson`/`getJson`, this
 * preserves the HTTP `status` and the parsed `{ error: { code } }` code so
 * callers can branch on it instead of matching message text.
 */
export class AgentResearchApiError extends Error {
  readonly status: number;
  readonly code: string | null;
  /**
   * The parsed JSON error body, when the API answered with one. Some refusals
   * carry the server-computed state that produced them (a not-ready readiness,
   * for one), and the caller must read that authority instead of re-deriving
   * the reason from the code.
   */
  readonly body: unknown;

  constructor(status: number, code: string | null, message: string, body: unknown = null) {
    super(message);
    this.name = "AgentResearchApiError";
    this.status = status;
    this.code = code;
    this.body = body;
  }
}

type AgentResearchRequestInit = {
  method: "DELETE" | "GET" | "POST" | "PUT";
  body?: unknown;
  headers?: HeadersInit | undefined;
};

async function agentResearchRequest<TResponse>(url: string, init: AgentResearchRequestInit): Promise<TResponse> {
  const headers = new Headers(init.headers);
  const hasBody = init.body !== undefined;
  if (hasBody) {
    headers.set("Content-Type", "application/json");
  }

  const response = await fetch(url, {
    method: init.method,
    headers,
    ...(hasBody ? { body: JSON.stringify(init.body) } : {})
  });

  if (!response.ok) {
    const text = await response.text();
    let code: string | null = null;
    let message = text;
    let body: unknown = null;
    try {
      const parsed = JSON.parse(text) as { error?: { code?: string; message?: string } };
      body = parsed;
      code = parsed.error?.code ?? null;
      message = parsed.error?.message ?? text;
    } catch {
      // Non-JSON error body (e.g. plain text / gateway error) — keep status, leave code null.
    }
    throw new AgentResearchApiError(
      response.status,
      code,
      message || `Request failed with ${response.status}`,
      body
    );
  }

  // 204 is a successful delete with no body; parsing it would throw.
  if (response.status === 204) {
    return undefined as TResponse;
  }

  return response.json() as Promise<TResponse>;
}

function browserLocalStorage(): SaleskoSessionGrantStorage | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }

  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

async function startBetterAuthProviderSignIn(provider: SaleskoOAuthProvider): Promise<boolean> {
  if (!authProviderAvailable(provider)) {
    return false;
  }

  const callbackURL = new URL(authCallbackPath, window.location.origin).toString();
  const response = await fetch(`${authApiBaseUrl}/api/auth/sign-in/social`, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      callbackURL,
      disableRedirect: true,
      errorCallbackURL: callbackURL,
      provider
    })
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Better Auth sign-in failed with ${response.status}`);
  }

  const body = (await response.json()) as BetterAuthSignInSocialResponse;
  if (typeof body.url !== "string" || !body.url) {
    throw new Error("better_auth_start_url_missing");
  }

  window.location.assign(body.url);
  return true;
}

function filenameFromContentDisposition(value: string | null) {
  const filename = value?.match(/filename="([^"]+)"/i)?.[1] ?? value?.match(/filename=([^;]+)/i)?.[1];
  return filename?.trim() || null;
}

function saleskoSessionAuthHeaders(
  session: StoredSaleskoSession | null = readStoredSaleskoSession()
): HeadersInit | undefined {
  if (!session) {
    return undefined;
  }

  return {
    Authorization: `${session.tokenType} ${session.token}`,
    "X-Salesko-Session-Tenant-Id": session.tenantId
  };
}

function notifyStoredSaleskoSessionChanged() {
  if (typeof window === "undefined" || typeof window.dispatchEvent !== "function") {
    return;
  }

  window.dispatchEvent(new Event(SaleskoSessionChangeEvent));
}

function parseStoredSessionGrantToken(value: string | null): string | null {
  const trimmed = value?.trim();
  if (!trimmed) {
    return null;
  }

  if (/^Bearer\s+/i.test(trimmed)) {
    return trimmed.replace(/^Bearer\s+/i, "").trim() || null;
  }

  if (!trimmed.startsWith("{")) {
    return trimmed;
  }

  try {
    const parsed = JSON.parse(trimmed) as unknown;
    if (!isRecord(parsed)) {
      return null;
    }

    return stringField(parsed, "token") ?? stringField(parsed, "accessToken") ?? stringField(parsed, "sessionToken");
  } catch {
    return null;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === "object" && !Array.isArray(value));
}

function stringField(record: Record<string, unknown>, key: string): string | null {
  const value = record[key];
  return typeof value === "string" && value.trim().length > 0 ? value.trim() : null;
}

function recordField(record: Record<string, unknown>, key: string): Record<string, unknown> {
  const value = record[key];
  return isRecord(value) ? value : {};
}

function hasAllowedSupabaseAuthProvider(payload: Record<string, unknown>) {
  const providers = supabaseAuthProviders(payload);
  return providers.length > 0 && providers.every((provider) => allowedSupabaseOAuthProviders.has(provider));
}

function supabaseAuthProviders(payload: Record<string, unknown>) {
  const appMetadata = recordField(payload, "app_metadata");
  const providers = new Set<string>();
  const provider = stringField(appMetadata, "provider");
  if (provider) {
    providers.add(provider);
  }

  const linkedProviders = appMetadata.providers;
  if (Array.isArray(linkedProviders)) {
    for (const linkedProvider of linkedProviders) {
      if (typeof linkedProvider === "string" && linkedProvider.trim().length > 0) {
        providers.add(linkedProvider.trim());
      }
    }
  }

  return [...providers];
}

function jwtPayload(token: string): Record<string, unknown> {
  const [, payload] = token.split(".");
  if (!payload) {
    return {};
  }

  try {
    const parsed = JSON.parse(base64UrlDecode(payload)) as unknown;
    return isRecord(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function base64UrlDecode(value: string) {
  const base64 = value.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");
  return atob(padded);
}

function supabaseExpiresAt(params: URLSearchParams, payload: Record<string, unknown>) {
  const expiresAt = params.get("expires_at");
  if (expiresAt && /^\d+$/.test(expiresAt)) {
    return new Date(Number(expiresAt) * 1000).toISOString();
  }

  const exp = payload.exp;
  if (typeof exp === "number" && Number.isFinite(exp)) {
    return new Date(exp * 1000).toISOString();
  }

  const expiresIn = params.get("expires_in");
  if (expiresIn && /^\d+$/.test(expiresIn)) {
    return new Date(Date.now() + Number(expiresIn) * 1000).toISOString();
  }

  return null;
}

function isStoredSaleskoSession(value: unknown): value is StoredSaleskoSession {
  if (!isRecord(value)) {
    return false;
  }

  return (
    value.tokenType === "Bearer" &&
    typeof value.token === "string" &&
    value.token.length > 0 &&
    typeof value.expiresAt === "string" &&
    typeof value.tenantId === "string" &&
    typeof value.userId === "string" &&
    (value.email === undefined || typeof value.email === "string") &&
    (value.name === undefined || typeof value.name === "string")
  );
}

function isPendingBillingCheckoutIntent(value: unknown): value is PendingBillingCheckoutIntent {
  if (!isRecord(value)) {
    return false;
  }

  return (
    typeof value.createdAt === "string" &&
    Number.isFinite(Date.parse(value.createdAt)) &&
    typeof value.productId === "string" &&
    value.productId.trim().length > 0 &&
    value.productId.length <= 80 &&
    typeof value.returnPath === "string" &&
    normalizeReturnPath(value.returnPath) === value.returnPath
  );
}

function normalizeReturnPath(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed.startsWith("/") || trimmed.startsWith("//")) {
    return null;
  }
  try {
    const parsed = new URL(trimmed, "https://app.salesko.ai");
    if (parsed.origin !== "https://app.salesko.ai") {
      return null;
    }
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return null;
  }
}
